import { pickBy } from 'lodash';
import fetchWithOptions from '../../../shared/utils/fetch';
import { PTags, QrsTags, WaveTag } from '../../wave/types/tag.type';
import { ServerWaveType } from '../../wave/types/wave.type';
import { User } from '../types/auth';
import {
  Preferences,
  ServerAnnotationSettingsTags,
  ServerPreferences,
} from '../types/preferences.type';

export const getPreferences = async (
  userId: User['id'],
): Promise<Preferences | null> => {
  try {
    const preferences = await fetchWithOptions<ServerPreferences>(
      `/api/setting/user/${userId}`,
    );

    return jsonToPreferences(preferences);
  } catch {
    return null;
  }
};

const jsonToPreferences = ({
  abnormalities_version,
  show_user_diagnostic_first,
  platane_show_shr,
  platane_enable_single_save_button,
  platane_save_delineation,
  platane_save_diagnostic,
  platane_auto_diagnostic,
  platane_auto_import_delineation,
  platane_show_algo_delineation,
  platane_show_algo_diagnostic,
  platane_show_coworker_delineation,
  platane_show_coworker_diagnostic,
  platane_show_laddergram,
  platane_annotation_settings,
}: ServerPreferences): Preferences => {
  return {
    annotationTable: abnormalities_version,
    showUserDiagnosticFirst: show_user_diagnostic_first,
    showShr: platane_show_shr ?? false,
    enableSingleSave: platane_enable_single_save_button ?? false,
    allowSaveDelineation: platane_save_delineation ?? true,
    allowSaveDiagnostic: platane_save_diagnostic ?? true,
    autoDiagnostic: platane_auto_diagnostic ?? false,
    autoImportDelineation: platane_auto_import_delineation ?? false,
    showAlgoDelineation: platane_show_algo_delineation ?? false,
    showAlgoDiagnostic: platane_show_algo_diagnostic ?? false,
    showCoworkerDelineation: platane_show_coworker_delineation ?? false,
    showCoworkerDiagnostic: platane_show_coworker_diagnostic ?? false,
    showLaddergram: platane_show_laddergram ?? true,
    wavesToDisplay:
      platane_annotation_settings != null
        ? wavesToList(platane_annotation_settings['waves_to_display'])
        : new Set(),
    wavesToAnnotate:
      platane_annotation_settings != null
        ? wavesToList(platane_annotation_settings['waves_to_annotate'])
        : new Set(),
    tagsToDisplay:
      platane_annotation_settings != null
        ? tagsToList(platane_annotation_settings['tags_to_display'])
        : { P: [], QRS: [] },
    tagsToAnnotate:
      platane_annotation_settings != null
        ? tagsToList(platane_annotation_settings['tags_to_annotate'])
        : { P: [], QRS: [] },
  };
};

export const wavesToList = (
  serverWaveTypes: Record<ServerWaveType, boolean> | null,
): Set<ServerWaveType> => {
  if (serverWaveTypes == null) {
    return new Set();
  }
  const waveTypes = Object.keys(pickBy(serverWaveTypes)) as ServerWaveType[];
  return new Set(waveTypes);
};

export const tagsToList = (
  annotationSettings: ServerAnnotationSettingsTags,
): WaveTag => {
  if (annotationSettings == null) {
    return {
      P: [],
      QRS: [],
    };
  }
  const pTags = Object.keys(pickBy(annotationSettings['P'])).map((tag) =>
    tag.toUpperCase(),
  ) as unknown as PTags[];
  const qrsTags = Object.keys(pickBy(annotationSettings['QRS'])).map((tag) =>
    tag.toUpperCase(),
  ) as unknown as QrsTags[];

  return {
    P: pTags,
    QRS: qrsTags,
    calibration: ['CALIBRATION'],
    artifact: ['ARTIFACT'],
    noise: ['NOISE'],
  };
};
